/**
 * 편집 중인 매핑 → 3D 대조 트랙과 그 옆에 둘 안내.
 *
 * 3D 는 폼에서 고른 역할(관측 또는 action)의 손을 모두 그린다. 편집 중인 손은 진하게, 나머지는 옅게 그린다.
 * 그릴 수 없는 손은 트랙을 만들지 않고 이유를 안내로 남긴다. 실패한 자세를 항등으로 그리지 않는다.
 *
 * Owns:
 * - 손 · 역할 → `ViewerTrack`(id · 이름 · 좌우 · 그리퍼 벌림 · 강조), 안내 문장.
 *
 * Does not own:
 * - 자세 계산 — `mappingPose.ts`. 그리기 — `sim/`.
 */
import type { InspectChannel } from "../domain/episodeInspect";
import type { Mapping } from "../domain/mapping";
import type { ViewerTrack } from "../domain/viewerTypes";
import { gripperOpening } from "./gripperOpening";
import type { MappingFocus } from "./mappingEditor";
import { HAND_ROLE_LABEL, HAND_SIDE_LABEL } from "./mappingPath";
import { buildRoleTrack, gripperProblemText, roleTrackProblemText } from "./mappingPose";
import { tcpOffset } from "./tcpPose";

export type MappingTracks = {
  readonly tracks: readonly ViewerTrack[];
  /** 그리지 못한 손 · 움직이지 않는 손가락 · 건너뛴 행의 이유. */
  readonly notes: readonly string[];
};

export function buildMappingTracks(
  mapping: Mapping,
  focus: MappingFocus,
  channels: ReadonlyMap<string, InspectChannel>,
): MappingTracks {
  if (mapping.hands.length === 0) {
    return { tracks: [], notes: ["손 구성을 고르면 3D 대조가 섭니다."] };
  }
  const tracks: ViewerTrack[] = [];
  const notes: string[] = [];
  mapping.hands.forEach((hand, index) => {
    const role = hand[focus.role];
    const label = `${HAND_SIDE_LABEL[hand.side]} ${HAND_ROLE_LABEL[focus.role]}`;
    if (role === null) {
      notes.push(`${label}: 매핑하지 않았습니다.`);
      return;
    }
    const result = buildRoleTrack(role, channels);
    if (!result.ok) {
      notes.push(`${label}: ${roleTrackProblemText(result.problem)}`);
      return;
    }
    const { gripper, series, source, skippedRows } = result.track;
    if (!gripper.ok) {
      notes.push(`${label}: ${gripperProblemText(gripper.problem)}`);
    }
    if (skippedRows > 0) {
      notes.push(
        `${label}: 무효 표시이거나 유한하지 않은 값의 행 ${skippedRows}개는 그리지 않았습니다.`,
      );
    }
    tracks.push({
      id: `${hand.side}:${focus.role}`,
      label,
      role: focus.role === "action" ? "target" : "observation",
      pairedTrackId: null,
      series,
      tcp: { source, role },
      // single 은 좌우가 없는 슬롯이다. 색이 한쪽 손이라고 말하지 않게 중립으로 둔다.
      side: hand.side === "single" ? null : hand.side,
      showOrientation: tcpOffset(role) !== null,
      gripper: gripper.ok ? gripperOpening(gripper.series) : null,
      emphasized: index === focus.handIndex,
    });
  });
  return { tracks, notes };
}
